/**
 * Tab Registry — Single source of truth for EntityDetailPanel tabs.
 *
 * ADDING A NEW TAB:
 * 1. Add one entry to TAB_REGISTRY below (id, label, icon, contextualFor)
 * 2. Create the tab component in ./tabs/ or the page-specific tabs/ folder
 * 3. Add the Tabs.Panel rendering block in EntityDetailPanel.tsx
 *
 * That's it. TAB_LABELS, TAB_ICONS, DEFAULT_TAB_ORDER, CONTEXTUAL_TABS
 * are all auto-derived from this single registry. No manual sync needed.
 */

import {
  IconEye,
  IconEdit,
  IconLink,
  IconHistory,
  IconTimeline,
  IconGitCompare,
  IconSitemap,
  IconCalendar,
  IconGitBranch,
  IconMap,
  IconFiles,
  IconNotes,
  IconArrowsExchange,
  IconTopologyFull,
} from '@tabler/icons-react';
import type { PrimitiveType } from '@/config/primitive-metadata';

// ============================================================================
// TYPES
// ============================================================================

/** All available detail panel tabs (derived from registry) */
export type DetailTab =
  | 'details'
  | 'edit'
  | 'relationships'
  | 'audit'
  | 'timeline'
  | 'versions'
  | 'context'
  | 'schedule'
  | 'dependencies'
  | 'workflow-map'
  | 'documents'
  | 'notes'
  | 'compare'
  | 'flow';

/** Single tab definition — the ONLY place you need to edit when adding a tab */
export interface TabDefinition {
  id: DetailTab;
  label: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: React.ComponentType<any>;
  /** If set, tab only appears for these primitive types. If omitted, always visible. */
  contextualFor?: PrimitiveType[];
}

// ============================================================================
// REGISTRY — Single source of truth
// ============================================================================

/**
 * Tab definitions in display order.
 * Adding a new tab? Add ONE entry here. Everything else is auto-derived.
 */
export const TAB_REGISTRY: TabDefinition[] = [
  { id: 'details',       label: 'Details',       icon: IconEye },
  { id: 'edit',          label: 'Edit',          icon: IconEdit },
  { id: 'relationships', label: 'Relationships', icon: IconLink },
  { id: 'audit',         label: 'Audit',         icon: IconHistory },
  { id: 'timeline',      label: 'Timeline',      icon: IconTimeline },
  { id: 'versions',      label: 'Versions',      icon: IconGitCompare },
  { id: 'context',       label: 'Context',       icon: IconSitemap,       contextualFor: ['task', 'instance', 'approval', 'checkpoint'] },
  { id: 'schedule',      label: 'Schedule',      icon: IconCalendar,      contextualFor: ['task'] },
  { id: 'dependencies',  label: 'Dependencies',  icon: IconGitBranch,     contextualFor: ['task'] },
  { id: 'workflow-map',  label: 'Workflow Map',   icon: IconMap,           contextualFor: ['task'] },
  { id: 'flow',          label: 'Flow',          icon: IconTopologyFull,  contextualFor: ['workflow', 'instance'] },
  { id: 'documents',     label: 'Documents',     icon: IconFiles },
  { id: 'notes',         label: 'Notes',         icon: IconNotes },
  { id: 'compare',       label: 'Compare',       icon: IconArrowsExchange },
];

// ============================================================================
// AUTO-DERIVED LOOKUPS — Never maintain these manually
// ============================================================================

/** Tab labels derived from registry */
export const TAB_LABELS: Record<DetailTab, string> = Object.fromEntries(
  TAB_REGISTRY.map(t => [t.id, t.label])
) as Record<DetailTab, string>;

/** Tab icons derived from registry */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const TAB_ICONS: Record<DetailTab, React.ComponentType<any>> = Object.fromEntries(
  TAB_REGISTRY.map(t => [t.id, t.icon])
// eslint-disable-next-line @typescript-eslint/no-explicit-any
) as Record<DetailTab, React.ComponentType<any>>;

/** Default tab order derived from registry (order = array order) */
export const DEFAULT_TAB_ORDER: DetailTab[] = TAB_REGISTRY.map(t => t.id);

/** Contextual tabs derived from registry */
export const CONTEXTUAL_TABS: Partial<Record<DetailTab, PrimitiveType[]>> = Object.fromEntries(
  TAB_REGISTRY
    .filter(t => t.contextualFor != null)
    .map(t => [t.id, t.contextualFor!])
) as Partial<Record<DetailTab, PrimitiveType[]>>;

// ============================================================================
// HELPERS
// ============================================================================

/** Get visible tabs for a given primitive type */
export function getVisibleTabs(primitiveType: PrimitiveType): DetailTab[] {
  return TAB_REGISTRY
    .filter(t => !t.contextualFor || t.contextualFor.includes(primitiveType))
    .map(t => t.id);
}
